import { Dispatch } from 'react'
import { handleRequest } from './base/handleRequest'
import { claims } from '../../claims'
import {
  API_APPRENTICES,
  API_COMPANIES_SHORT,
  API_GRADE_SCHEMAS,
  API_GROUPS,
  API_GROUPS_SHORT,
  API_PERSONS_BY_ACCOUNT,
  API_PERSONS_BY_ACCOUNT_SHORT,
  API_PROFESSIONS,
} from '../../constants'
import { GradeSchema } from '../../model/apprenticeModuleGrade.model'
import { Company } from '../../model/company.model'
import { RequestMethod, UserAccountType } from '../../model/enums/enums'
import { Group } from '../../model/group.model'
import { Apprentice, ApprenticeBody, Person } from '../../model/person.model'
import { Profession } from '../../model/profession.model'
import { State } from '../../model/state.model'
import { allApprenticesSet } from '../../redux/basic/apprentice/apprenticeReducer'
import { setCompanies } from '../../redux/basic/company/companyActions'
import { allEmployeesSet } from '../../redux/basic/employee/employeeReducer'
import { setGradeSchemas } from '../../redux/basic/grade-schema/gradeSchemaActions'
import { addGroup } from '../../redux/basic/group/groupActions'
import {
  decrementLoadingCounter,
  incrementLoadingCounter,
} from '../../redux/basic/loading/loadingActions'
import { personsSet } from '../../redux/basic/person/personReducer'
import { professionsSet } from '../../redux/basic/profession/professionReducer'
import { basicInformationLoaded } from '../../redux/basic/user/userSlice'
import { getPersonFullName } from '../../util/getPersonName'
import { hasRequiredClaim } from '../../util/validation/hasClaim'

const loadAllCompanies = async (dispatcher: Dispatch<any>) => {
  const companies = await handleRequest<Company[]>(
    API_COMPANIES_SHORT(),
    RequestMethod.GET
  )
  if (companies.data) {
    dispatcher(setCompanies(companies.data))
  } else {
    console.error('failed to load companies')
  }
}

const loadApprenticeData = async (dispatcher: Dispatch<any>) => {
  const apprenticeData: State<ApprenticeBody[]> = await handleRequest(
    API_APPRENTICES(),
    RequestMethod.GET
  )
  const apprentices = await handleRequest<any>(
    API_PERSONS_BY_ACCOUNT_SHORT(UserAccountType.Apprentice),
    RequestMethod.GET
  )

  const res: Apprentice[] = []
  apprentices?.data?.forEach((entry: Apprentice) => {
    const temp = {
      ...entry,
      full_name: getPersonFullName(entry),
      apprentice: apprenticeData?.data?.find(
        (ad: any) => ad.id_person === entry.person_id
      ),
      user_account_type: UserAccountType.Apprentice,
    }
    res.push(temp)
  })
  dispatcher(allApprenticesSet(res))
}

const loadAllEmployees = async (dispatcher: Dispatch<Person>) => {
  const res: Person[] = []
  const admins = await handleRequest<Person[]>(
    API_PERSONS_BY_ACCOUNT_SHORT(UserAccountType.Admin),
    RequestMethod.GET
  )

  const berufsbildner = await handleRequest<Person[]>(
    API_PERSONS_BY_ACCOUNT_SHORT(UserAccountType.Berufsbildner),
    RequestMethod.GET
  )

  const praxisbildner = await handleRequest<Person[]>(
    API_PERSONS_BY_ACCOUNT_SHORT(UserAccountType.Praxisbildner),
    RequestMethod.GET
  )

  function setEmployeeIntoList(p: Person, type: UserAccountType): void {
    p.user_account_type = type
    p.full_name = getPersonFullName(p)
    res.push(p)
  }

  admins.data?.map((p) => {
    setEmployeeIntoList(p, UserAccountType.Admin)
  })
  berufsbildner.data?.map((p) => {
    setEmployeeIntoList(p, UserAccountType.Berufsbildner)
  })
  praxisbildner.data?.map((p) => {
    setEmployeeIntoList(p, UserAccountType.Praxisbildner)
  })
  if (res.length > 0) {
    dispatcher(allEmployeesSet(res))
  }
}

const loadAllGroups = async (
  userClaims: string[],
  dispatcher: Dispatch<any>
) => {
  if (hasRequiredClaim(userClaims, claims.hrm.groups.getAll)) {
    const result = await handleRequest<Group[]>(API_GROUPS(), RequestMethod.GET)
    const groups = result.data
    if (groups) {
      for (let group of groups) {
        dispatcher(addGroup(group))
      }
    }
  } else {
    const groups = await handleRequest<Group[]>(
      API_GROUPS_SHORT(),
      RequestMethod.GET
    )
    groups?.data?.forEach((group: Group) => {
      // 1 = the group is active
      // if (group.is_active === 1) {
      dispatcher(addGroup(group))
      // }
    })
  }
}

const loadAllAccountlessPersons = async (
  userClaims: string[],
  dispatcher: Dispatch<any>
) => {
  if (hasRequiredClaim(userClaims, claims.hrm.persons.getAllWithoutAccount)) {
    //TODO create short in backend for accountLess ppl
    const persons = await handleRequest<Person[]>(
      API_PERSONS_BY_ACCOUNT(UserAccountType.NoAccount),
      RequestMethod.GET
    )
    const ppl: Person[] = []
    persons?.data?.forEach((entry: Person) => {
      if (entry.is_active === 1) {
        entry.user_account_type = UserAccountType.NoAccount
        entry.full_name = getPersonFullName(entry)
        ppl.push(entry)
      }
    })
    dispatcher(personsSet(ppl))
  }
}

const loadAllProfessions = async (dispatcher: Dispatch<any>) => {
  const professions = await handleRequest<Profession[]>(
    API_PROFESSIONS(),
    RequestMethod.GET
  )
  if (professions.data) {
    dispatcher(professionsSet(professions.data))
  } else {
    console.warn('Could not load professions.')
  }
}

const loadAllGradeSchemas = async (dispatcher: Dispatch<any>) => {
  const schemas = await handleRequest<GradeSchema[]>(
    API_GRADE_SCHEMAS(),
    RequestMethod.GET
  )

  dispatcher(setGradeSchemas(schemas.data))
}

export const loadBasicInformation = async (
  userClaims: string[],
  dispatcher: Dispatch<any>
): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    dispatcher(incrementLoadingCounter())
    const loacCompanies = loadAllCompanies(dispatcher)
    const loadApprentices = loadApprenticeData(dispatcher)
    const loadEmployees = loadAllEmployees(dispatcher)
    const loadGroups = loadAllGroups(userClaims, dispatcher)
    const loadAccountlessPersons = loadAllAccountlessPersons(
      userClaims,
      dispatcher
    )
    const loadProfessions = loadAllProfessions(dispatcher)
    const loadGradeSchemas = loadAllGradeSchemas(dispatcher)

    Promise.all([
      loacCompanies,
      loadApprentices,
      loadEmployees,
      loadGroups,
      loadAccountlessPersons,
      loadProfessions,
      loadGradeSchemas,
      8,
    ])
      .then(() => {
        dispatcher(decrementLoadingCounter())
        dispatcher(basicInformationLoaded(true))
        return resolve(true)
      })
      .catch(() => {
        dispatcher(decrementLoadingCounter())
        dispatcher(basicInformationLoaded(false))
        return resolve(false)
      })
  })
}